import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { RouterLink } from '@angular/router';

import { CallbackNotificationComponent, SidenavComponent } from '@components';
import { LetDirective } from '@ngrx/component';
import { DataService } from '@services';
import { KanbanBoardHelperService, KanbanBoardStore, TutorialsStore, TutorialType } from '@store';
import {
  ViContentComponent,
  ViInfoPanelComponent,
  ViLoaderComponent,
  ViPageComponent,
} from '@vi-iot/ui';
import { SideNavMode } from '@vi-iot/ui/layouts/sidenav/sidenav.component';
import { combineLatest, Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { ViDswFooterComponent } from '../../shared/+design-system-web/vi-dsw-footer/vi-dsw-footer.component';
import { CommonStandaloneModule } from '../../shared/common-standalone.module';
import { ShepherdModalService } from '../../shepherd/shepherd-modal.service';
import { KanbanDashboardColumnsComponent } from './kanban-installation-list-columns/kanban-installation-list-columns.component';
import { KanbanInstallationListHeaderComponent } from './kanban-installation-list-header/kanban-installation-list-header.component';
import { StatusPageComponent } from 'src/app/shared/components/status-page/status-page.component';

@Component({
  selector: 'app-kanban-installation-list',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    KanbanDashboardColumnsComponent,
    KanbanInstallationListHeaderComponent,
    KanbanDashboardColumnsComponent,
    CallbackNotificationComponent,
    ViInfoPanelComponent,
    SidenavComponent,
    ViPageComponent,
    ViContentComponent,
    ViLoaderComponent,
    RouterLink,
    MatButtonModule,
    LetDirective,
    ViDswFooterComponent,
    StatusPageComponent,
  ],
  templateUrl: './kanban-installation-list.component.html',
  styleUrls: ['./kanban-installation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KanbanInstallationListComponent implements OnInit, OnDestroy {
  readonly sideSideNavMode = SideNavMode.side;
  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    public kanbanBoardStore: KanbanBoardStore,
    public dataService: DataService,
    private kanbanBoardHelperService: KanbanBoardHelperService,
    private shepherdModalService: ShepherdModalService,
    private tutorialsStore: TutorialsStore
  ) {}

  ngOnInit(): void {
    this.kanbanBoardStore.clearSelectedFilters();
    this.kanbanBoardStore.fetchDashboard();

    combineLatest([
      this.kanbanBoardStore.fetchingDashboardStatus$,
      this.kanbanBoardStore.predefinedFilters$,
    ])
      .pipe(
        filter(
          ([fetchingStatus, predefinedFilters]) =>
            fetchingStatus.loaded && predefinedFilters.length > 0
        ),
        first(),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([_fetchingStatus, filters]) => {
        this.kanbanBoardHelperService.filtersSelectedInDropdown$.next(filters);
        this.kanbanBoardStore.applyDashboardFilter();
      });

    this.tutorialsStore.displayKanbanBoardTutorial$
      .pipe(
        filter((displayTutorial) => !!displayTutorial),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((displayTutorial) => {
        if (displayTutorial) {
          this.shepherdModalService.startTutorial(TutorialType.KanbanBoard);
        }
      });
  }

  ngOnDestroy(): void {
    this.kanbanBoardStore.clearPredefinedDashboardFilters();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
